import React from "react";
import {
  ArrowRight, ArrowUpRight, ArrowLeft, RefreshCw, ShieldCheck, Rocket, TrendingUp,
  Activity, Star, Award, Headset, Radar, Plug, Languages, Layers, Palette, Smartphone,
  RadioTower, Satellite, Truck, KeyRound, MapPinned, Handshake, Tag, Quote, FileText,
  SearchX, Send, Check, Menu, X, Users, Globe, Wrench, HeartHandshake, Mail, Phone,
  MapPin, CircleAlert, Sparkles, Loader, Cpu, Server, LayoutDashboard, Archive, Compass,
  Circle, Share2, AtSign
} from "lucide-react";

/* Kebab-case name -> Lucide component (matches the prototype's <Icon name="…" />). */
const ICON_MAP = {
  "arrow-right": ArrowRight, "arrow-up-right": ArrowUpRight, "arrow-left": ArrowLeft,
  "refresh-cw": RefreshCw, "shield-check": ShieldCheck, rocket: Rocket, "trending-up": TrendingUp,
  activity: Activity, star: Star, award: Award, headset: Headset, radar: Radar, plug: Plug,
  languages: Languages, layers: Layers, palette: Palette, smartphone: Smartphone,
  "radio-tower": RadioTower, satellite: Satellite, truck: Truck, "key-round": KeyRound,
  "map-pinned": MapPinned, handshake: Handshake, tag: Tag, quote: Quote, "file-text": FileText,
  "search-x": SearchX, send: Send, check: Check, menu: Menu, x: X, users: Users, globe: Globe,
  wrench: Wrench, "heart-handshake": HeartHandshake, mail: Mail, phone: Phone, "map-pin": MapPin,
  "circle-alert": CircleAlert, sparkles: Sparkles, loader: Loader, cpu: Cpu, server: Server,
  "layout-dashboard": LayoutDashboard, archive: Archive, compass: Compass, circle: Circle,
  "share-2": Share2, "at-sign": AtSign
};

export function Icon({ name, size = 20, color = "currentColor", strokeWidth = 1.75, style, className }) {
  const Cmp = ICON_MAP[name] || Circle;
  return (
    <Cmp
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      style={style}
      className={className}
      aria-hidden="true"
    />
  );
}

/* Standard centered container. */
export function Container({ children, narrow, style }) {
  return (
    <div
      style={{
        width: "100%",
        maxWidth: narrow ? "var(--container-narrow)" : "var(--container-max)",
        margin: "0 auto",
        padding: "0 var(--gutter)",
        ...style
      }}
    >
      {children}
    </div>
  );
}

/* Section wrapper with tonal background variants. */
export function Section({ children, tone = "page", pad = "var(--space-12)", id, style }) {
  const bg =
    {
      page: "var(--surface-page)",
      wash: "var(--surface-wash)",
      tint: "var(--surface-tint)",
      ink: "var(--brand-ink)"
    }[tone] || "transparent";
  return (
    <section id={id} style={{ background: bg, padding: `${pad} 0`, ...style }}>
      <Container>{children}</Container>
    </section>
  );
}

export function Eyebrow({ children, onDark, style }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-sans)",
        fontWeight: 700,
        fontSize: "var(--text-xs)",
        letterSpacing: "var(--tracking-widest)",
        textTransform: "uppercase",
        color: onDark ? "var(--red-300)" : "var(--brand-red)",
        ...style
      }}
    >
      {children}
    </div>
  );
}

/* CSS keyframe entrance (transform-only; content stays visible). */
export function Reveal({ children, delay = 0, y = 18, as = "div", style, className, ...rest }) {
  const Tag = as;
  return (
    <Tag
      className={"rw-rise" + (className ? " " + className : "")}
      style={{ animationDelay: delay + "ms", "--rw-rise-y": y + "px", ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Number that counts up (defaults to final value under reduced motion). */
export function CountUp({ value, style }) {
  const [display, setDisplay] = React.useState(value);
  React.useEffect(() => {
    const m = String(value).match(/^([^\d]*)([\d.]+)(.*)$/);
    if (!m) {
      setDisplay(value);
      return;
    }
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof requestAnimationFrame === "undefined") {
      setDisplay(value);
      return;
    }
    const [, pre, num, post] = m;
    const target = parseFloat(num);
    const decimals = (num.split(".")[1] || "").length;
    let raf;
    let cancelled = false;
    const start = performance.now();
    const dur = 1100;
    let progressed = false;
    setDisplay(pre + (0).toFixed(decimals) + post);
    const tick = (now) => {
      if (cancelled) return;
      progressed = true;
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(pre + (target * eased).toFixed(decimals) + post);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const watchdog = setTimeout(() => {
      if (!progressed) setDisplay(value);
    }, 400);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(watchdog);
    };
  }, [value]);
  return <span style={style}>{display}</span>;
}

/* Red-accented portion of a display heading. */
export function Hi({ children }) {
  return <span style={{ color: "var(--brand-red)" }}>{children}</span>;
}

/* Design-system button: flat red primary with subtle lift. */
export function Button({
  children,
  variant = "primary",
  size = "md",
  iconLeft = null,
  iconRight = null,
  fullWidth = false,
  disabled = false,
  type = "button",
  onClick,
  style,
  ...rest
}) {
  const sizes = {
    sm: { height: "var(--control-h-sm)", padding: "0 var(--space-4)", font: "var(--text-sm)", gap: "var(--space-2)", radius: "var(--radius-sm)" },
    md: { height: "var(--control-h-md)", padding: "0 var(--space-5)", font: "var(--text-sm)", gap: "var(--space-2)", radius: "var(--radius-md)" },
    lg: { height: "var(--control-h-lg)", padding: "0 var(--space-6)", font: "var(--text-md)", gap: "var(--space-3)", radius: "var(--radius-md)" }
  };
  const s = sizes[size] || sizes.md;
  const variants = {
    primary: { background: "var(--brand-red)", color: "var(--text-on-brand)", border: "none", boxShadow: "var(--shadow-brand)" },
    secondary: { background: "var(--surface-card)", color: "var(--text-strong)", border: "1.5px solid var(--border-default)", boxShadow: "var(--shadow-xs)" },
    ghost: { background: "transparent", color: "var(--text-brand)", border: "1.5px solid transparent", boxShadow: "none" },
    dark: { background: "var(--brand-ink)", color: "var(--text-on-brand)", border: "none", boxShadow: "var(--shadow-md)" }
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: fullWidth ? "flex" : "inline-flex",
        width: fullWidth ? "100%" : "auto",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: "var(--weight-bold)",
        lineHeight: 1,
        letterSpacing: "-0.01em",
        borderRadius: s.radius,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition:
          "transform var(--duration-fast) var(--ease-out), filter var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)",
        whiteSpace: "nowrap",
        ...v,
        ...style
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = "translateY(1px) scale(0.99)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = ""; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.filter = ""; }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.filter = "brightness(1.06)"; }}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}
